import React, { useEffect, useState } from "react";
import { RotaCheckbox, RotaButton } from "@teamrota/rota-design";
import DatePicker from "react-datepicker";
import { useMutation } from "@apollo/client";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";

import {
  StyledSwitchHeaderContainer,
  StyledNumberInput,
  StyledRowContainer,
  StyledInput,
  StyledP,
  AntSwitch,
  StyledSubHeading,
  StyledSmallBoldP,
  StyledInfo,
  StyledInfoBox,
  StyledBoldHeader,
  StyledButtonsContainer,
  StyledErrorMessage
} from "./styles";

import UPDATE_MEMBER_AVAILABILITY from "./graphql/update-member-availability";
import ExceptionDates from "./exception-dates";

const LimitedHours = ({ member }) => {
  if (!member?.id) return;

  const [isLimitedHours, setIsLimitedHours] = useState(member.isLimitedHours);
  const [limitedHours, setLimitedHours] = useState(member.limitedHours || 0);
  const [isLimitedHoursExpire, setIsLimitedHoursExpire] = useState(
    member.isLimitedHoursExpire
  );
  const [limitedHoursExpireAt, setLimitedHoursExpireAt] = useState(
    member?.limitedHoursExpireAt
      ? new Date(member.limitedHoursExpireAt)
      : new Date()
  );
  const [errors, setErrors] = useState({});

  const [snack, setSnack] = useState(null);

  const [updateMemberAvailability] = useMutation(UPDATE_MEMBER_AVAILABILITY);

  const handleSave = async () => {
    setSnack({ severity: "warning", message: "Saving..." });
    try {
      await updateMemberAvailability({
        variables: {
          id: member.id,
          ...{
            isLimitedHours,
            limitedHours,
            isLimitedHoursExpire,
            limitedHoursExpireAt
          }
        }
      });
      setSnack({ severity: "success", message: "Saved!" });
    } catch (e) {
      setSnack({ severity: "error", message: "something went wrong" });
    }
  };

  useEffect(() => {
    setErrors(e => {
      const errors = { ...e };
      if (limitedHours > 48) errors.limitedHours = "max 48 hours allowed";
      else delete errors.limitedHours;
      return errors;
    });
  }, [limitedHours]);

  useEffect(() => {
    setErrors(e => {
      const errors = { ...e };
      if (isLimitedHoursExpire && limitedHoursExpireAt < new Date())
        errors.limitedHoursExpireAt = "expiry must be in the future";
      else delete errors.limitedHoursExpireAt;
      return errors;
    });
  }, [limitedHoursExpireAt, isLimitedHoursExpire]);

  return (
    <>
      <StyledSwitchHeaderContainer>
        <AntSwitch
          name="checkedC"
          checked={isLimitedHours}
          onChange={() => setIsLimitedHours(isLimitedHours => !isLimitedHours)}
        />
        <StyledBoldHeader>Limited working hours</StyledBoldHeader>
      </StyledSwitchHeaderContainer>
      <StyledRowContainer>
        <StyledSubHeading>
          Member will not be able to see or apply to shifts taking them over the
          limit from their mobile app.
        </StyledSubHeading>
      </StyledRowContainer>
      <StyledRowContainer>
        <StyledSubHeading>
          However, they are not guaranteed to fall under this cap. Partners can
          overload a member with additional shifts at their discretion. Also,
          members are not removed from shifts in excess of the limit if a limit
          is enabled after the shifts have been assigned.
        </StyledSubHeading>
      </StyledRowContainer>
      <StyledP disabled={!isLimitedHours}>Enter limitations:</StyledP>
      <StyledRowContainer>
        <StyledSubHeading disabled={!isLimitedHours}>
          Maximum hours allowed to work
        </StyledSubHeading>
        <StyledNumberInput
          min={0}
          disabled={!isLimitedHours}
          onChange={e => setLimitedHours(parseInt(e.target.value))}
          type="number"
          value={limitedHours}
        />
        <StyledSmallBoldP disabled={!isLimitedHours}>hrs/week</StyledSmallBoldP>
        <StyledErrorMessage>{errors.limitedHours}</StyledErrorMessage>
      </StyledRowContainer>
      <StyledRowContainer style={{ paddingLeft: "10px" }}>
        <RotaCheckbox
          isChecked={isLimitedHoursExpire}
          isDisabled={!isLimitedHours}
          onClick={() =>
            setIsLimitedHoursExpire(
              isLimitedHoursExpire => !isLimitedHoursExpire
            )
          }
        />
        <StyledSmallBoldP disabled={!isLimitedHours}>
          Ending date
        </StyledSmallBoldP>
      </StyledRowContainer>
      <StyledRowContainer>
        <StyledP disabled={!isLimitedHours}>Specify ending date</StyledP>
        <DatePicker
          placeholderText={limitedHoursExpireAt || "End date"}
          onChange={date => {
            setLimitedHoursExpireAt(new Date(date));
          }}
          selected={limitedHoursExpireAt}
          todayButton={true}
          showYearDropdown
          adjustDateOnChange
          dateFormat="dd/MM/yyyy"
          disabled={!isLimitedHours || !isLimitedHoursExpire}
          customInput={<StyledInput />}
        />
        <StyledErrorMessage>{errors.limitedHoursExpireAt}</StyledErrorMessage>
      </StyledRowContainer>
      <StyledRowContainer>
        <StyledInfo disabled={!isLimitedHours}>i</StyledInfo>
        <StyledInfoBox>
          On exemption days, the worker can work unlimited hours, which do not
          count towards their weekly cap.
        </StyledInfoBox>
        <StyledSubHeading disabled={!isLimitedHours}>
          Add exemption dates:
        </StyledSubHeading>
      </StyledRowContainer>

      <ExceptionDates
        memberId={member.id}
        memberAvailabilityExceptions={member.memberAvailabilityExceptions}
        isLimitedHours={isLimitedHours}
      />

      <StyledButtonsContainer>
        <RotaButton
          disabled={!!Object.keys(errors).length}
          onClick={handleSave}
        >
          Save
        </RotaButton>
      </StyledButtonsContainer>
      {!!snack && (
        <Snackbar
          open={!!snack}
          autoHideDuration={3000}
          onClose={() => setSnack(null)}
        >
          <Alert onClose={() => setSnack(null)} severity={snack.severity}>
            {snack.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default LimitedHours;
